import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A defensive wire entanglement: a row of crossed wooden picket stakes
// (X-frames seen from the side) with dark wire strands strung along the row
// and small knots for the barbs. Runs the full tile width along X.
const TIMBER = "#8b6438";
const WIRE = "#3a3a38";

const STAKE_X = [-0.36, -0.12, 0.12, 0.36] as const;
const CROSS_Y = 0.16; // where each pair of stakes crosses
const LEAN = 0.6; // stake lean (radians) either side of vertical

// One X-frame: two timber stakes crossing in the YZ plane at x.
function xFrame(x: number): Part[] {
  return ([LEAN, -LEAN] as const).map((tilt) => ({
    geometry: new THREE.CylinderGeometry(0.018, 0.022, 0.36, 6), color: TIMBER,
    position: [x, CROSS_Y, 0] as const, rotation: [tilt, 0, 0] as const,
  }));
}

// One wire strand running the full row along X, with barb knots on it.
function strand(y: number, z: number, barbs: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.CylinderGeometry(0.008, 0.008, 0.9, 5), color: WIRE,
      position: [0, y, z], rotation: [0, 0, Math.PI / 2] },
  ];
  for (let i = 0; i < barbs; i++) {
    const x = -0.3 + (i / (barbs - 1)) * 0.6 + (i % 2 === 0 ? 0.05 : -0.05);
    out.push({ geometry: new THREE.IcosahedronGeometry(0.016, 0), color: WIRE, position: [x, y, z] });
  }
  return out;
}

export const barbedWire = defineModel({
  id: "barbed-wire",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  author: "Fable 5",
  name: "Barbed Wire",
  tags: ["prop", "wwi", "military", "defense", "obstacle"],
  summary: "Crossed picket stakes strung with barbed-wire strands.",
  footprint: 0.45,
  parts(): Part[] {
    return [
      ...STAKE_X.flatMap(xFrame),
      // Top strand riding in the V of the X-frames, side strands on the legs.
      ...strand(0.28, 0, 4),
      ...strand(0.11, 0.13, 3),
      ...strand(0.11, -0.13, 3),
      ...strand(0.04, 0.16, 0),
      ...strand(0.04, -0.16, 0),
    ];
  },
  animations: [],
});
